import { isTenantIntegrationDisconnectEnabled } from "common/tenantIntegrationPermissionUtils"
import { isTenantOwnerAdmin } from "utils/isTenantOwnerAdmin"
import {
  isPermissionImplied,
  isSubPermissionImplied,
} from "utils/permissionDependencies"

const TENANT_ADMIN_SUB_PERMISSION_BYPASS_EXCLUDE = new Set([
  "combo_access_view",
  "modifier_access_view",
  "view_device_information",
  "view_orders",
  "view_loyalty_points",
  "view_payment_history",
  "view_wallet_history",
  "view_gift_card_usage",
  "view_rider_overview",
  "view_rider_status_history",
  "view_rider_documents",
  "view_rider_orders",
  "view_rider_earnings",
  "view_rider_payments",
  "view_rider_ratings",
  "view_rider_location",
  // Branch fulfillment toggles are plan-gated; tenant admin must not force them on.
  "enable_pickup",
  "enable_delivery",
  "enable_curbside",
  "enable_drive_thru",
  "enable_dine_in",
  "enable_pickup_curbside",
  "enable_dine_drive_thru",
  "link_discount_with_pos",
  "link_menu_with_pos",
  "enable_erp_integration",
  "generate_order_qr",
  "city_zones_map",
  "city_map",
  // Menu item OOS / enable-disable are plan-gated; tenant admin must not force them on.
  "enable_out_of_stock",
  "enable_disable_item",
  "enable_out_of_stock_modifier",
])

const isPermissionEnabled = (
  userRole,
  categoryId,
  permissionId,
  accountType,
  accountScope
) => {
  const scope =
    accountScope ||
    (typeof localStorage !== "undefined"
      ? localStorage.getItem("accountScope")
      : null)
  const tenantId =
    typeof localStorage !== "undefined"
      ? localStorage.getItem("tenantId")
      : null
  // Keep full bypass only for the legacy bootstrap superadmin account.
  // Super-admin panel users (accountScope=super_admin) must respect role permissions.
  if (
    accountType === "superadmin" &&
    scope !== "tenant" &&
    !tenantId
  ) {
    return true
  }

  if (
    (accountScope === "super_admin" || accountType === "superadmin") &&
    !tenantId &&
    Number(categoryId) === 11
  ) {
    return true
  }

  const categoryAliases = {
    3: [102], // tenant Roles -> SA Roles (fallback for legacy seeded role data)
    43: [42], // Zones: legacy category id before canonical 43
    48: [107], // tenant Integrations <-> SA Integrations (same logical module)
    107: [48],
  }
  const permissionAliases = {
    roles_1: ["sa_roles_1"],
    roles_2: ["sa_roles_2"],
    roles_3: ["sa_roles_3"],
    roles_4: ["sa_roles_4"],
    tenant_integrations_1: ["sa_integrations_1"],
    tenant_integrations_2: ["sa_integrations_2", "sa_integrations_2_add"],
    tenant_integrations_3: [
      "sa_integrations_3",
      "sa_integrations_2",
      "sa_integrations_2_edit",
      "sa_integrations_3_edit",
    ],
    tenant_integrations_4: [
      "sa_integrations_4",
      "sa_integrations_2",
      "sa_integrations_4_integration",
    ],
    tenant_integrations_5: ["sa_integrations_2", "sa_integrations_2_status"],
  }

  if (userRole && userRole.permissions) {
    if (permissionId === "tenant_integrations_5") {
      if (isTenantIntegrationDisconnectEnabled(userRole)) return true
    }
    const candidateCategoryIds = [categoryId, ...(categoryAliases[categoryId] || [])]
    const roleWithRoleCategory = userRole.permissions.find(role =>
      candidateCategoryIds.some(id => Number(role.id) === Number(id))
    )

    if (roleWithRoleCategory) {
      const candidatePermissionIds = [
        permissionId,
        ...(permissionAliases[permissionId] || []),
      ]
      const permission = roleWithRoleCategory.permissions.find(permission =>
        candidatePermissionIds.includes(permission.id)
      )
      if (permission?.isEnabled) {
        return true
      }

      if (
        isPermissionImplied(userRole.permissions, categoryId, permissionId)
      ) {
        return true
      }

      for (const entry of roleWithRoleCategory.permissions) {
        const subPermissions = Array.isArray(entry?.subPermissions)
          ? entry.subPermissions
          : []
        const subPermission = subPermissions.find(sub =>
          candidatePermissionIds.includes(sub.id)
        )
        if (subPermission?.isEnabled) {
          return true
        }
      }
    }

    if (isPermissionImplied(userRole.permissions, categoryId, permissionId)) {
      return true
    }
  }
  return false
}

const SA107_INTEGRATION_ACTIONS = new Set([
  "sa_integrations_2_add",
  "sa_integrations_2_edit",
  "sa_integrations_2_status",
])
const SA107_DELETE_ACTIONS = new Set([
  "sa_integrations_4_integration",
  "sa_integrations_4_category",
])
const SA107_CATEGORY_ACTIONS = new Set([
  "sa_integrations_3_add",
  "sa_integrations_3_edit",
])

/**
 * Category 107: granular catalog permission rows. Legacy roles may still use
 * `sa_integrations_2` / `sa_integrations_3` / `sa_integrations_4` only.
 */
export const isSaIntegrationActionEnabled = (
  userRole,
  actionPermissionId,
  accountType,
  accountScope
) => {
  const scope =
    accountScope ||
    (typeof localStorage !== "undefined"
      ? localStorage.getItem("accountScope")
      : null)
  const tenantId =
    typeof localStorage !== "undefined"
      ? localStorage.getItem("tenantId")
      : null
  if (
    accountType === "superadmin" &&
    scope !== "tenant" &&
    !tenantId
  ) {
    return true
  }

  if (!userRole?.permissions) return false
  const category = userRole.permissions.find(
    r => Number(r.id) === 107 || r.id === 107
  )
  if (!category?.permissions?.length) return false

  const byId = Object.fromEntries(category.permissions.map(p => [p.id, p]))
  const row = byId[actionPermissionId]
  if (row?.isEnabled) return true

  const p2 = byId.sa_integrations_2
  if (p2?.isEnabled && SA107_INTEGRATION_ACTIONS.has(actionPermissionId)) {
    return true
  }
  if (p2?.subPermissions?.length) {
    const sub = p2.subPermissions.find(s => s.id === actionPermissionId)
    if (sub?.isEnabled) return true
  }

  const p4 = byId.sa_integrations_4
  if (p4?.isEnabled && SA107_DELETE_ACTIONS.has(actionPermissionId)) {
    return true
  }
  if (p4?.subPermissions?.length) {
    const sub = p4.subPermissions.find(s => s.id === actionPermissionId)
    if (sub?.isEnabled) return true
  }

  const p3 = byId.sa_integrations_3
  if (p3?.isEnabled && SA107_CATEGORY_ACTIONS.has(actionPermissionId)) {
    return true
  }
  if (p3?.subPermissions?.length) {
    const sub = p3.subPermissions.find(s => s.id === actionPermissionId)
    if (sub?.isEnabled) return true
  }

  return false
}

export const getSubPermissions = (
  userRole,
  categoryId,
  permissionId,
  accountType,
  accountScope
) => {
  const scope =
    accountScope ||
    (typeof localStorage !== "undefined"
      ? localStorage.getItem("accountScope")
      : null)
  if (userRole && userRole.permissions) {
    const roleWithRoleCategory = userRole.permissions.find(
      role => Number(role.id) === Number(categoryId)
    )
    if (roleWithRoleCategory) {
      const permission = roleWithRoleCategory.permissions.find(
        permission => permission.id === permissionId
      )

      if (permission && permission.subPermissions) {
        const subPermissionsData = permission.subPermissions.reduce(
          (acc, subPermission) => {
            const bypassAdminForSub =
              isTenantOwnerAdmin() &&
              !TENANT_ADMIN_SUB_PERMISSION_BYPASS_EXCLUDE.has(subPermission.id)
            const impliedSub = isSubPermissionImplied(
              userRole.permissions,
              categoryId,
              permissionId,
              subPermission.id
            )
            acc[subPermission.id] = {
              ...subPermission,
              isEnabled: bypassAdminForSub
                ? true
                : Boolean(
                    (permission.isEnabled && subPermission.isEnabled) ||
                      impliedSub
                  ),
            }
            return acc
          },
          {}
        )
        return subPermissionsData
      } else {
        return permission && permission.isEnabled
      }
    }
  }
  return false
}

export default isPermissionEnabled
